/**
 * WP 9.4 — a `Proposal` (`chatProtocol.ts`), as a chip's label and as the
 * plan `components/chat/Proposal.tsx` runs when it is tapped.
 *
 * `labelFor` is a thin wrapper over `chatClient.ts`'s own `proposalLabel`
 * (built by 9.2, and not this package's file to change) rather than a
 * second copy of the same switch — the brief's exact signature
 * (`titles: Map<string, string>` in place of a lookup function) is what
 * this file adds, and the words themselves stay the one place 9.2 already
 * wrote them.
 *
 * `planFor` is pure: given only the `Proposal`, it says which mutation
 * fires and with which of the action's own fields, or that the caller has
 * to resolve something first (`needs: 'showing'` — no `starts_at` yet;
 * `needs: 'list'` — no `list_id` yet). It cannot resolve either on its
 * own: that needs the event's showings or Doug's lists, which live in the
 * query cache, not in the proposal — `Proposal.tsx` reads `Plan['needs']`
 * and does the rest.
 *
 * WP 9.7 §2 — `useAddItem`'s own `addItem` takes only `body` and `qty`; a
 * proposal's `link`, `price_cents`, `currency` and `image_url` (what
 * research found, for the treadmill flow) never reached the row. An
 * `add_item` plan now carries a `followUp`: the fields, if any, `Proposal.tsx`
 * writes with `updateItem`/`setItemFields` once the insert has an id.
 * `undefined` when the proposal named none — no follow-up write for a plain
 * grocery item.
 */
import type { ListKind } from './types'
import type { Proposal } from './chatProtocol'
import { proposalLabel } from './chatClient.ts'

/** `Save · Tosca`, `Add to Purchases · Treadmill X`, `Rate · Tosca · 4 stars`,
 *  `Remember`, `Open Browse` — the chip's own words, from `chatClient.ts`'s
 *  `proposalLabel`, adapted from a lookup function to the `Map` this
 *  package's brief names. */
export function labelFor(p: Proposal, titles: Map<string, string>): string {
  return proposalLabel(p, (id) => titles.get(id) ?? null)
}

/** The columns `useAddItem`'s `addItem` cannot write itself — see the
 *  follow-up note above. Matches `db.ts`'s `updateItem` (`link`) plus the
 *  three of `ListItemFields` a proposal can carry. */
export type AddItemFollowUp = { link?: string; price_cents?: number; currency?: string; image_url?: string }

export type Plan =
  | { hook: 'save'; entryId: string }
  | { hook: 'schedule'; entryId: string; startsAt: string }
  | { hook: 'schedule'; needs: 'showing'; entryId: string }
  | { hook: 'rate'; entryId: string; stars: 1 | 2 | 3 | 4 | 5 | null }
  | {
      hook: 'add_item'
      listId: string
      body: string
      qty?: string
      followUp?: AddItemFollowUp
    }
  | {
      hook: 'add_item'
      needs: 'list'
      listKind: ListKind
      body: string
      qty?: string
      followUp?: AddItemFollowUp
    }
  | { hook: 'note'; body: string; noteKind: 'fix' | 'change' | 'idea'; entryId?: string; venueId?: string }
  | { hook: 'remember'; body: string; expiresOn?: string }
  | { hook: 'open'; screen: 'browse' | 'year' | 'lists' | 'city'; id?: string; query?: Record<string, string> }
  // WP 9.9 — a grant row, then the question the chip carries, asked again.
  | { hook: 'spend_more'; amountUsd: number; day: string; retryText: string }
  // WP 9.12 — no mutation at all: a tap sends one of `texts` as Doug's next
  // message (the question's options, or the follow-ups). `freeText` says
  // whether typing instead also answers the question — it changes the
  // composer's placeholder, and what the next message reports.
  | { hook: 'send'; texts: string[]; freeText: boolean }

/** An `add_item` proposal's `link`/`price_cents`/`currency`/`image_url`, as
 *  the one follow-up write `Proposal.tsx` makes after `useAddItem`'s insert
 *  — or `undefined` when the proposal named none, so a plain grocery item
 *  gets no second round trip. */
function addItemFollowUp(p: Extract<Proposal, { kind: 'add_item' }>): AddItemFollowUp | undefined {
  const f: AddItemFollowUp = {}
  if (p.link !== undefined) f.link = p.link
  if (p.price_cents !== undefined) f.price_cents = p.price_cents
  if (p.currency !== undefined) f.currency = p.currency
  if (p.image_url !== undefined) f.image_url = p.image_url
  return Object.keys(f).length ? f : undefined
}

/** Which mutation a tap on this chip runs, with which arguments — or that
 *  something is missing first. An unknown `kind` (never a real `Proposal`,
 *  only ever a value that slipped past `isProposal`'s own check) answers
 *  `null` rather than throwing: a chip for garbage draws nothing sooner
 *  than it crashes the thread around it. */
export function planFor(p: Proposal): Plan | null {
  switch (p.kind) {
    case 'save':
      return { hook: 'save', entryId: p.entry_id }
    case 'schedule':
      return p.starts_at == null
        ? { hook: 'schedule', needs: 'showing', entryId: p.entry_id }
        : { hook: 'schedule', entryId: p.entry_id, startsAt: p.starts_at }
    case 'rate':
      return { hook: 'rate', entryId: p.entry_id, stars: p.stars }
    case 'add_item': {
      const followUp = addItemFollowUp(p)
      return p.list_id == null
        ? { hook: 'add_item', needs: 'list', listKind: p.list_kind, body: p.body, qty: p.qty, followUp }
        : { hook: 'add_item', listId: p.list_id, body: p.body, qty: p.qty, followUp }
    }
    case 'note':
      return { hook: 'note', body: p.body, noteKind: p.note_kind, entryId: p.entry_id, venueId: p.venue_id }
    case 'remember':
      return { hook: 'remember', body: p.body, expiresOn: p.expires_on }
    case 'open':
      return { hook: 'open', screen: p.screen, id: p.id, query: p.query }
    case 'spend_more':
      return { hook: 'spend_more', amountUsd: p.amount_usd, day: p.day, retryText: p.retry_text }
    case 'ask':
      return { hook: 'send', texts: p.options, freeText: p.allow_free_text === true }
    case 'followups':
      return { hook: 'send', texts: p.questions, freeText: false }
    default:
      return null
  }
}
